/**
 * The small set of pieces every screen is built from.
 *
 * Kept deliberately few. A component library grown one special case at a time
 * is how a UI stops looking like one thing.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../lib/api";

/* ------------------------------------------------------------------- chip */

export type Tone = "neutral" | "accent" | "ok" | "warn" | "crit" | "info";

export function Chip({
  tone = "neutral",
  dot = false,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`chip chip-${tone}`}>
      {dot && <i className="chip-dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------- card */

export function Card({
  title,
  subtitle,
  actions,
  children,
  padded = true,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  padded?: boolean;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          {title && <h2>{title}</h2>}
          {subtitle && <span className="card-sub">{subtitle}</span>}
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      <div className={padded ? "card-body" : undefined}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ tiles */

export function Tile({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
}) {
  return (
    <div className="tile">
      <span className="tile-label">{label}</span>
      <span className={`tile-value${tone ? ` text-${tone}` : ""}`}>{value}</span>
      {detail && <span className="tile-detail">{detail}</span>}
    </div>
  );
}

export function Tiles({ children }: { children: ReactNode }) {
  return <div className="tiles">{children}</div>;
}

/* ------------------------------------------------------------- page state */

export function Loading({ what = "Loading" }: { what?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <p>{what}…</p>
    </div>
  );
}

/**
 * Something went wrong — or, quite often, nothing did.
 *
 * A screen that needs the server and cannot reach it is not an error. Since
 * the device carries its own store, the honest reading of a transport failure
 * is "this particular screen is desk work", not "Keystone is broken", and
 * showing a red alarm for a van in a tunnel teaches people to dismiss red
 * alarms. So the two cases are told apart here, once, rather than at eight
 * call sites that would drift.
 */
export function ErrorState({
  error,
  onRetry,
  needs,
}: {
  error: unknown;
  onRetry?: () => void;
  /** What this screen needs a connection *for*, in the user's terms. */
  needs?: string;
}) {
  const offline = error instanceof ApiError && error.status === 0;

  if (offline) {
    return (
      <div className="state state-offline" role="status">
        <p className="state-title">No connection</p>
        <p className="tiny">
          {needs ?? "This screen reads from the office and needs a signal."} Your own
          jobs, the job card and the kit calculator all work without one.
        </p>
        {onRetry && (
          <button className="btn btn-sm" onClick={onRetry} type="button">
            Try again
          </button>
        )}
      </div>
    );
  }

  const message =
    error instanceof Error ? error.message : "Something went wrong loading this.";
  return (
    <div className="state state-error" role="alert">
      <p className="state-title">{message}</p>
      {onRetry && (
        <button className="btn" onClick={onRetry} type="button">
          Try again
        </button>
      )}
    </div>
  );
}

export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <p className="state-title">{title}</p>
      {children && <p className="state-body">{children}</p>}
      {action}
    </div>
  );
}

/**
 * The honest placeholder.
 *
 * Every module in the feature matrix has a route, but several have no API
 * behind them yet. Rather than render a convincing screen full of invented
 * numbers, say what is missing and name the card that will build it — so the
 * UI never implies the system can do something it cannot.
 */
export function NotBuiltYet({
  module,
  card,
  what,
  depends,
}: {
  module: string;
  card: string;
  what: string;
  depends?: string;
}) {
  return (
    <div className="notbuilt">
      <span className="notbuilt-card">{card}</span>
      <h2>{module}</h2>
      <p>{what}</p>
      {depends && <p className="notbuilt-dep">Waiting on {depends}.</p>}
      <p className="notbuilt-foot">
        The screen is routed and the shell is real — there is no API behind it yet,
        so nothing invented is shown here.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ table */

export function DataTable({
  columns,
  children,
}: {
  columns: { key: string; label: string; align?: "left" | "right"; hideOnPhone?: boolean }[];
  children: ReactNode;
}) {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`${c.align === "right" ? "right" : ""} ${c.hideOnPhone ? "phone-hide" : ""}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------- misc */

export function Bar({ value, tone = "accent" }: { value: number; tone?: Tone }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="bar" role="presentation">
      <i className={`bar-fill bar-${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function RowLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link className="row-link" to={to}>
      {children}
    </Link>
  );
}

export function PageHeader({
  eyebrow,
  title,
  actions,
  children,
}: {
  eyebrow?: string;
  title: string;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="page-head">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {children && <p className="page-sub">{children}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}
